import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Loader2 } from "lucide-react";
import apiClient from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function notificationTarget(n, role) {
  const et = n.entity_type;
  if (et === "booking") return role === "owner" ? "/owner/bookings" : "/customer/bookings";
  if (et === "conversation") return `${role === "owner" ? "/owner" : "/customer"}/messages?c=${n.entity_id}`;
  if (et === "property") return role === "admin" ? `/admin/properties/${n.entity_id}` : `/owner/properties/${n.entity_id}`;
  return "/notifications";
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef();

  const loadCount = useCallback(() => {
    apiClient.get("/notifications/unread-count").then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    apiClient.get("/notifications?limit=10").then((r) => setItems(r.data?.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 20000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const onItem = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await apiClient.post(`/notifications/${n.id}/read`); loadCount(); } catch { /* ignore */ }
    }
    navigate(notificationTarget(n, user?.role));
  };

  const markAll = async () => {
    try { await apiClient.post("/notifications/read-all"); setUnread(0); loadList(); } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell"
        className="relative grid place-items-center w-10 h-10 rounded-full text-primary hover:bg-secondary/60 transition-colors">
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center text-[10px] font-bold rounded-full bg-accent text-accent-foreground">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-card shadow-xl overflow-hidden z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-heading font-semibold text-sm">Notifikasi</span>
            <button onClick={markAll} data-testid="notification-mark-all"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              <Check size={13} /> Tandai semua
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
            {loading ? (
              <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" size={20} /></div>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">Belum ada notifikasi.</div>
            ) : (
              items.map((n, i) => (
                <button key={n.id} onClick={() => onItem(n)} data-testid={`notification-item-${i}`}
                  className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/50 transition-colors ${n.is_read ? "" : "bg-secondary/30"}`}>
                  <div className="flex items-start gap-2">
                    {!n.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0" />}
                    <div className={`min-w-0 ${n.is_read ? "pl-4" : ""}`}>
                      <p className="text-sm font-semibold truncate">{n.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <button onClick={() => { setOpen(false); navigate("/notifications"); }} data-testid="notification-see-all"
            className="w-full py-2.5 text-center text-sm font-semibold text-primary border-t border-border hover:bg-secondary/50 transition-colors">
            Lihat semua
          </button>
        </div>
      )}
    </div>
  );
}
